/**
 * Application shell — header plus the three responsive panes.
 *
 * Layout rules live in CSS; this component only computes which pane owns a
 * narrow viewport (`data-mobile-pane`), derived from the user's actual
 * selection depth: folders, then list, then reader.
 */
import type { JSX } from "preact";
import { useState } from "preact/hooks";

import { brandFromHostname } from "../branding.ts";
import { deliverOutbox } from "../state/session.ts";
import {
  activeFolderId,
  activeMessageId,
  connection,
  composeRequest,
  queuedCount,
  selectMessage,
} from "../state/store.ts";

import { ComposeDialog } from "./compose-dialog.tsx";
import { FolderSidebar } from "./folder-sidebar.tsx";
import { BackIcon, ComposeIcon, SettingsIcon } from "./icons.tsx";
import { MessageList, SearchBox } from "./message-list.tsx";
import { MessageReader } from "./message-reader.tsx";
import { SettingsDialog } from "./settings-dialog.tsx";

/**
 * Which pane fills a narrow screen; deeper selections take priority.
 */
type MobilePane = "folders" | "list" | "reader";

/**
 * Renders the application chrome and panes.
 *
 * @returns The shell element.
 */
export function Shell(): JSX.Element {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const brand = brandFromHostname(location.hostname);
  const mobilePane: MobilePane =
    activeMessageId.value === null
      ? activeFolderId.value === null
        ? "folders"
        : "list"
      : "reader";
  return (
    <div class="shell" data-mobile-pane={mobilePane}>
      <header class="app-header">
        <span class="brand">{brand.title}</span>
        <div class="header-actions">
          {connection.value === "connected" ? (
            <button
              type="button"
              class="primary-button compose-button"
              onClick={() => {
                composeRequest.value = { mode: "new" };
              }}
            >
              <ComposeIcon />
              Compose
            </button>
          ) : null}
          <button
            type="button"
            class="icon-button"
            onClick={() => {
              setSettingsOpen(true);
            }}
          >
            <SettingsIcon title="Settings" />
          </button>
        </div>
      </header>
      {connection.value === "error" ? (
        <p class="alert-banner" role="alert">
          The mail bridge is unreachable right now. Cached mail is still
          available; use Settings to retry the connection.
        </p>
      ) : null}
      <OutboxStrip />
      {/* `main` gives assistive tech the primary landmark for the mail
          surface itself; header and banners stay outside it. */}
      <main class="panes">
        <aside class="pane pane-folders" aria-label="Folders">
          <FolderSidebar />
        </aside>
        <section class="pane pane-list" aria-label="Messages">
          <div class="pane-toolbar">
            <button
              type="button"
              class="icon-button back-button"
              onClick={() => {
                activeFolderId.value = null;
              }}
            >
              <BackIcon title="Back to folders" />
            </button>
            <span class="pane-title">Messages</span>
            <SearchBox />
          </div>
          <MessageList />
        </section>
        <section class="pane pane-reader" aria-label="Message">
          <div class="pane-toolbar">
            <button
              type="button"
              class="icon-button back-button"
              onClick={() => {
                selectMessage(null);
              }}
            >
              <BackIcon title="Back to messages" />
            </button>
            <span class="pane-title">Reading</span>
          </div>
          <MessageReader />
        </section>
      </main>
      <ComposeDialog />
      <SettingsDialog
        open={settingsOpen}
        onClose={() => {
          setSettingsOpen(false);
        }}
      />
    </div>
  );
}

/**
 * Outbox strip shown while messages wait to be delivered.
 *
 * @returns The strip element, or `null` when the outbox is empty.
 */
function OutboxStrip(): JSX.Element | null {
  if (queuedCount.value === 0) {
    return null;
  }
  const noun = queuedCount.value === 1 ? "message" : "messages";
  return (
    <div class="outbox-strip" role="status">
      <span>
        {queuedCount.value} {noun} queued to send.
      </span>
      <button
        type="button"
        class="ghost-button"
        onClick={() => {
          void deliverOutbox();
        }}
      >
        Send now
      </button>
    </div>
  );
}
